import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import {
  getGastosFijos, crearGastoFijo, actualizarGastoFijo, borrarGastoFijo,
  CATEGORIAS_GASTO, TASAS_IVA, calcularConIva, calcularCostoNetoReal, categoriaLabel,
} from '../../lib/contabilidadApi'

const fmt = (n) => '$' + Math.round(Number(n || 0)).toLocaleString('es-UY')

export default function GastosFijos() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null) // null | { id?, ...form }

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    setItems(await getGastosFijos())
    setLoading(false)
  }

  const totales = useMemo(() => {
    const act = items.filter(g => g.activo)
    let conIva = 0, deducible = 0, creditoIva = 0
    for (const g of act) {
      const c = calcularCostoNetoReal(g)
      conIva += c
      if (g.es_deducible_irae) deducible += c
      if (g.tiene_credito_fiscal_iva) {
        creditoIva += (Number(g.monto_neto) - Number(g.monto_descuento || 0)) * (Number(g.tasa_iva) / 100)
      }
    }
    return { cantidad: act.length, conIva, deducible, creditoIva: Math.round(creditoIva) }
  }, [items])

  async function guardar(form) {
    try {
      if (form.id) await actualizarGastoFijo(form.id, form)
      else await crearGastoFijo(form)
      setModal(null)
      load()
    } catch (e) { alert('Error: ' + e.message) }
  }

  async function borrar(item) {
    if (!confirm(`¿Borrar "${item.descripcion}"? Esta acción no se puede deshacer.`)) return
    try {
      await borrarGastoFijo(item.id)
      load()
    } catch (e) { alert('Error: ' + e.message) }
  }

  async function toggleActivo(item) {
    try {
      await actualizarGastoFijo(item.id, { activo: !item.activo })
      load()
    } catch (e) { alert('Error: ' + e.message) }
  }

  async function validarPorRoberto(item) {
    try {
      await actualizarGastoFijo(item.id, { estado_validacion: 'validado_roberto' })
      load()
    } catch (e) { alert('Error: ' + e.message) }
  }

  return (
    <AdminLayout>
      <div className="gf-wrap">
        <div className="gf-headline">
          <div>
            <h1>Gastos fijos mensuales</h1>
            <p>Egresos recurrentes para el modelo financiero y asientos contables.</p>
          </div>
          <button className="gf-btn-primary" onClick={() => setModal({})}>+ Nuevo gasto</button>
        </div>

        {/* KPIs */}
        <div className="gf-kpis">
          <KpiCard label="Gastos activos" value={totales.cantidad} sub="cantidad" />
          <KpiCard label="Costo total mensual" value={fmt(totales.conIva)} sub="c/IVA neto de descuentos" color="#dc2626" />
          <KpiCard label="Deducible de IRAE" value={fmt(totales.deducible)} sub="baja base imponible" color="#16a34a" />
          <KpiCard label="Crédito fiscal IVA" value={fmt(totales.creditoIva)} sub="descontable de IVA débito" color="#2563eb" />
        </div>

        {/* Tabla */}
        {loading ? (
          <div className="gf-loading">Cargando…</div>
        ) : (
          <div className="gf-tabla-wrap">
            <table className="gf-tabla">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Descripción</th>
                  <th>Categoría</th>
                  <th style={{ textAlign: 'right' }}>Neto</th>
                  <th style={{ textAlign: 'right' }}>IVA</th>
                  <th style={{ textAlign: 'right' }}>C/IVA</th>
                  <th style={{ textAlign: 'right' }}>Descuento</th>
                  <th style={{ textAlign: 'right' }}>Costo real</th>
                  <th style={{ textAlign: 'center' }}>IRAE</th>
                  <th style={{ textAlign: 'center' }}>IVA cred.</th>
                  <th style={{ textAlign: 'center' }}>Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((g, i) => {
                  const conIva = calcularConIva(g.monto_neto, g.tasa_iva)
                  const costoReal = calcularCostoNetoReal(g)
                  const esBorrador = g.estado_validacion === 'borrador'
                  return (
                    <tr key={g.id} className={!g.activo ? 'gf-row-inactiva' : ''}>
                      <td>{i + 1}</td>
                      <td>
                        <div className="gf-desc">{g.descripcion}</div>
                        {g.proveedor && <div className="gf-prov">{g.proveedor}</div>}
                        {esBorrador && <span className="gf-pill-borrador">⏳ borrador</span>}
                        {g.periodicidad && g.periodicidad !== 'mensual' && (
                          <span style={{ fontSize: 10, marginLeft: 4, padding: '1px 6px', borderRadius: 999, background: '#dbeafe', color: '#1e40af', fontWeight: 700 }}>
                            🗓 {g.periodicidad} {g.criterio_imputacion === 'devengado' ? '· devengado' : '· caja'}
                          </span>
                        )}
                      </td>
                      <td><span className="gf-pill-cat">{categoriaLabel(g.categoria_contable)}</span></td>
                      <td style={{ textAlign: 'right' }}>{fmt(g.monto_neto)}</td>
                      <td style={{ textAlign: 'right' }}>{g.tasa_iva}%</td>
                      <td style={{ textAlign: 'right' }}>{fmt(conIva)}</td>
                      <td style={{ textAlign: 'right', color: '#16a34a' }}>
                        {Number(g.monto_descuento) > 0 ? `-${fmt(calcularConIva(g.monto_descuento, g.tasa_iva))}` : '—'}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 800 }}>{fmt(costoReal)}</td>
                      <td style={{ textAlign: 'center' }}>{g.es_deducible_irae ? '✓' : '—'}</td>
                      <td style={{ textAlign: 'center' }}>{g.tiene_credito_fiscal_iva ? '✓' : '—'}</td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className={`gf-toggle ${g.activo ? 'on' : 'off'}`}
                          onClick={() => toggleActivo(g)}
                          title={g.activo ? 'Desactivar' : 'Activar'}
                        >
                          {g.activo ? '🟢' : '⚫'}
                        </button>
                      </td>
                      <td>
                        {esBorrador && (
                          <button className="gf-btn-ok" onClick={() => validarPorRoberto(g)} title="Roberto valida este gasto — sale de borrador">
                            ✓ OK Roberto
                          </button>
                        )}
                        <button className="gf-btn-edit" onClick={() => setModal(g)}>Editar</button>
                        <button className="gf-btn-del" onClick={() => borrar(g)}>×</button>
                      </td>
                    </tr>
                  )
                })}
                {items.length === 0 && (
                  <tr><td colSpan={12} style={{ textAlign: 'center', padding: 30, color: '#94a3b8' }}>No hay gastos fijos cargados todavía.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Info contextual */}
        <div className="gf-info">
          <strong>📚 Tips de uso:</strong>
          <ul>
            <li><b>Monto neto</b> es SIN IVA (base imponible). Para gastos sin IVA (BPS, IRAE) = monto total.</li>
            <li><b>Descuento</b> sirve para casos como Biller con beneficio fiscal DGI: pagamos $1.671 c/IVA pero DGI nos descuenta $700 → costo real $971 c/IVA.</li>
            <li><b>Anticipo IRAE NO es gasto deducible</b> — es un pago a cuenta del IRAE anual (activo). Marcalo como "Anticipos a cuenta" y desactivá "deducible IRAE".</li>
            <li><b>Crédito fiscal IVA</b>: el IVA pagado se descuenta del IVA débito de ventas. Gastos sin IVA (BPS, sueldos) no aplican.</li>
          </ul>
        </div>
      </div>

      {modal && <ModalGasto initial={modal} onClose={() => setModal(null)} onSave={guardar} />}

      <style>{CSS}</style>
    </AdminLayout>
  )
}

function KpiCard({ label, value, sub, color = '#1A3461' }) {
  return (
    <div className="gf-kpi">
      <div className="gf-kpi-label">{label}</div>
      <div className="gf-kpi-value" style={{ color }}>{value}</div>
      {sub && <div className="gf-kpi-sub">{sub}</div>}
    </div>
  )
}

function ModalGasto({ initial, onClose, onSave }) {
  const [f, setF] = useState({
    descripcion: '',
    proveedor: '',
    monto_neto: 0,
    tasa_iva: 22,
    monto_descuento: 0,
    descuento_motivo: '',
    categoria_contable: 'gastos_administrativos',
    cuenta_contable_codigo: '',
    es_deducible_irae: true,
    tiene_credito_fiscal_iva: true,
    dia_devengamiento: 1,
    periodicidad: 'mensual',
    criterio_imputacion: 'devengado',
    mes_pago: null,
    notas: '',
    activo: true,
    orden: 0,
    ...initial,
  })
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value })
  const upNum = (k) => (e) => setF({ ...f, [k]: Number(e.target.value || 0) })
  const upBool = (k) => (e) => setF({ ...f, [k]: e.target.checked })

  function submit(e) {
    e.preventDefault()
    if (!f.descripcion.trim()) { alert('Cargá una descripción'); return }
    if (!f.monto_neto || f.monto_neto <= 0) { alert('Monto neto debe ser mayor a 0'); return }
    onSave({
      ...f,
      monto_neto: Number(f.monto_neto),
      monto_descuento: Number(f.monto_descuento || 0),
      tasa_iva: Number(f.tasa_iva),
      dia_devengamiento: Number(f.dia_devengamiento),
      orden: Number(f.orden),
      mes_pago: f.periodicidad === 'mensual' ? null : (Number(f.mes_pago) || null),
    })
  }

  const conIva = calcularConIva(f.monto_neto, f.tasa_iva)
  const descConIva = calcularConIva(f.monto_descuento, f.tasa_iva)
  const costoReal = conIva - descConIva

  return (
    <div className="gf-modal-bg" onClick={onClose}>
      <div className="gf-modal" onClick={(e) => e.stopPropagation()}>
        <button className="gf-modal-close" onClick={onClose} aria-label="Cerrar">×</button>
        <h2>{f.id ? 'Editar gasto fijo' : 'Nuevo gasto fijo'}</h2>

        <form onSubmit={submit}>
          <div className="gf-form-row">
            <label className="gf-field">
              <span>Descripción *</span>
              <input type="text" value={f.descripcion} onChange={up('descripcion')} placeholder="Ej: Seguro mensual SAS" required />
            </label>
            <label className="gf-field">
              <span>Proveedor</span>
              <input type="text" value={f.proveedor || ''} onChange={up('proveedor')} placeholder="Ej: Banco Itaú" />
            </label>
          </div>

          <div className="gf-form-row">
            <label className="gf-field">
              <span>Monto neto (sin IVA) *</span>
              <input type="number" min={0} step={0.01} value={f.monto_neto} onChange={upNum('monto_neto')} required />
            </label>
            <label className="gf-field">
              <span>Tasa IVA</span>
              <select value={f.tasa_iva} onChange={upNum('tasa_iva')}>
                {TASAS_IVA.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            <label className="gf-field">
              <span>Descuento (opcional)</span>
              <input type="number" min={0} step={0.01} value={f.monto_descuento || 0} onChange={upNum('monto_descuento')} />
            </label>
          </div>

          {Number(f.monto_descuento) > 0 && (
            <label className="gf-field">
              <span>Motivo del descuento</span>
              <input type="text" value={f.descuento_motivo || ''} onChange={up('descuento_motivo')} placeholder="Ej: Beneficio fiscal DGI" />
            </label>
          )}

          {/* Cálculo visible */}
          <div className="gf-calc">
            <span><b>{fmt(conIva)}</b> c/IVA</span>
            {Number(f.monto_descuento) > 0 && <span>− {fmt(descConIva)} descuento</span>}
            <span>= <b style={{ color: '#dc2626' }}>{fmt(costoReal)}</b> costo real neto</span>
          </div>

          <div className="gf-form-row">
            <label className="gf-field">
              <span>Categoría contable *</span>
              <select value={f.categoria_contable} onChange={up('categoria_contable')}>
                {CATEGORIAS_GASTO.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <div className="gf-hint">{CATEGORIAS_GASTO.find(c => c.value === f.categoria_contable)?.desc}</div>
            </label>
            <label className="gf-field">
              <span>Cuenta contable</span>
              <input type="text" value={f.cuenta_contable_codigo || ''} onChange={up('cuenta_contable_codigo')} placeholder="Ej: 5.3.01" />
              <div className="gf-hint">Del plan de cuentas. Se completará automático cuando se conecte Zeta.</div>
            </label>
          </div>

          <div className="gf-form-row">
            <label className="gf-check">
              <input type="checkbox" checked={f.es_deducible_irae} onChange={upBool('es_deducible_irae')} />
              <span>Es deducible de IRAE</span>
              <div className="gf-hint">Desactivar para anticipos a cuenta (anticipo IRAE) o gastos no deducibles.</div>
            </label>
            <label className="gf-check">
              <input type="checkbox" checked={f.tiene_credito_fiscal_iva} onChange={upBool('tiene_credito_fiscal_iva')} />
              <span>Tiene crédito fiscal IVA</span>
              <div className="gf-hint">Si tiene IVA y nos lo podemos descontar del IVA débito.</div>
            </label>
          </div>

          <div className="gf-form-row">
            <label className="gf-field">
              <span>Periodicidad del pago</span>
              <select value={f.periodicidad} onChange={up('periodicidad')}>
                <option value="mensual">Mensual</option>
                <option value="bimestral">Bimestral</option>
                <option value="trimestral">Trimestral</option>
                <option value="semestral">Semestral</option>
                <option value="anual">Anual</option>
              </select>
            </label>
            <label className="gf-field">
              <span>Criterio contable</span>
              <select value={f.criterio_imputacion} onChange={up('criterio_imputacion')}>
                <option value="devengado">Devengado (distribuir en N meses)</option>
                <option value="caja">Caja (todo el mes del pago)</option>
              </select>
            </label>
            {f.periodicidad !== 'mensual' && (
              <label className="gf-field">
                <span>Mes del pago (1-12)</span>
                <input type="number" min={1} max={12} value={f.mes_pago || ''} onChange={upNum('mes_pago')} placeholder="Ej: 1 = enero" />
              </label>
            )}
          </div>

          {f.periodicidad !== 'mensual' && (
            <div className="gf-help" style={{ background:'#f0f9ff', border:'1px solid #bae6fd', padding:'8px 12px', borderRadius:8, fontSize:12, color:'#075985', marginBottom:12 }}>
              💡 {f.criterio_imputacion === 'devengado'
                ? `Devengado: el gasto de $${Number(f.monto_neto || 0).toLocaleString('es-UY')} se reparte en ${f.periodicidad === 'anual' ? 12 : f.periodicidad === 'semestral' ? 6 : f.periodicidad === 'trimestral' ? 3 : 2} meses (${Math.round(Number(f.monto_neto || 0) / (f.periodicidad === 'anual' ? 12 : f.periodicidad === 'semestral' ? 6 : f.periodicidad === 'trimestral' ? 3 : 2)).toLocaleString('es-UY')}/mes). El IVA se asienta solo en el mes ${f.mes_pago || '?'}.`
                : `Caja: todo el monto + IVA va al mes ${f.mes_pago || '?'} del año.`}
            </div>
          )}

          <div className="gf-form-row">
            <label className="gf-field">
              <span>Día devengamiento (1-28)</span>
              <input type="number" min={1} max={31} value={f.dia_devengamiento} onChange={upNum('dia_devengamiento')} />
            </label>
            <label className="gf-field">
              <span>Orden de aparición</span>
              <input type="number" min={0} value={f.orden} onChange={upNum('orden')} />
            </label>
            <label className="gf-check">
              <input type="checkbox" checked={f.activo} onChange={upBool('activo')} />
              <span>Activo</span>
            </label>
          </div>

          <label className="gf-field">
            <span>Notas internas</span>
            <textarea rows={2} value={f.notas || ''} onChange={up('notas')} placeholder="Aclaraciones, condiciones especiales, etc." />
          </label>

          <div className="gf-form-actions">
            <button type="button" className="gf-btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="gf-btn-primary">{f.id ? 'Guardar cambios' : 'Crear gasto'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

const CSS = `
.gf-wrap { padding: 16px 20px 32px; max-width: 1400px; margin: 0 auto }
.gf-headline { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; flex-wrap: wrap; margin-bottom: 18px }
.gf-headline h1 { font-size: 26px; font-weight: 900; color: #1A3461; margin: 0 0 4px; letter-spacing: -.02em }
.gf-headline p { font-size: 13px; color: #64748b; margin: 0 }

.gf-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; margin-bottom: 18px }
.gf-kpi { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px }
.gf-kpi-label { font-size: 11px; color: #94a3b8; text-transform: uppercase; font-weight: 700; letter-spacing: .04em }
.gf-kpi-value { font-size: 22px; font-weight: 900; margin-top: 2px }
.gf-kpi-sub { font-size: 11px; color: #64748b; margin-top: 2px }

.gf-loading { padding: 40px; text-align: center; color: #94a3b8 }

.gf-tabla-wrap { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: auto }
.gf-tabla { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 1200px }
.gf-tabla th { text-align: left; padding: 12px 10px; background: #f8fafc; font-weight: 700; color: #475569; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid #e2e8f0; position: sticky; top: 0 }
.gf-tabla td { padding: 10px; border-bottom: 1px solid #f1f5f9; vertical-align: top }
.gf-tabla tr:last-child td { border-bottom: 0 }
.gf-tabla tr:hover { background: #f8fafc }
.gf-row-inactiva { opacity: .5 }

.gf-desc { font-weight: 700; color: #1A3461 }
.gf-prov { font-size: 11px; color: #64748b; margin-top: 2px }
.gf-pill-cat { display: inline-block; padding: 3px 8px; background: #e0e7ff; color: #3730a3; border-radius: 6px; font-size: 11px; font-weight: 700; white-space: nowrap }
.gf-pill-borrador { display: inline-block; margin-top: 4px; padding: 2px 6px; background: #fef3c7; color: #92400e; border-radius: 4px; font-size: 10px; font-weight: 800 }

.gf-toggle { background: transparent; border: 0; cursor: pointer; font-size: 14px }

.gf-btn-edit { background: transparent; border: 1px solid #cbd5e1; color: #475569; padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 700; cursor: pointer; margin-right: 4px }
.gf-btn-edit:hover { background: #f1f5f9; border-color: #94a3b8 }
.gf-btn-ok { background: #16a34a; color: #fff; border: 0; padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 800; cursor: pointer; margin-right: 6px; transition: background .15s }
.gf-btn-ok:hover { background: #15803d }
.gf-btn-del { background: transparent; border: 1px solid #fecaca; color: #b91c1c; padding: 4px 9px; border-radius: 6px; font-weight: 800; cursor: pointer }
.gf-btn-del:hover { background: #fee2e2 }

.gf-btn-primary { background: #1A3461; color: #fff; border: 0; padding: 9px 18px; border-radius: 8px; font-size: 13px; font-weight: 800; cursor: pointer; font-family: inherit; transition: background .15s }
.gf-btn-primary:hover { background: #0f2147 }
.gf-btn-secondary { background: #fff; border: 1px solid #cbd5e1; color: #475569; padding: 9px 18px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit }
.gf-btn-secondary:hover { background: #f8fafc }

.gf-info { margin-top: 20px; padding: 14px 18px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; font-size: 12.5px; color: #1e3a8a; line-height: 1.5 }
.gf-info strong { font-size: 13px; color: #1e3a8a }
.gf-info ul { margin: 8px 0 0; padding-left: 20px }
.gf-info li { margin-bottom: 4px }
.gf-info b { color: #1e40af }

/* Modal */
.gf-modal-bg { position: fixed; inset: 0; background: rgba(15,31,61,.55); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 16px }
.gf-modal { background: #fff; border-radius: 14px; max-width: 720px; width: 100%; max-height: 92vh; overflow-y: auto; padding: 26px 28px; position: relative; box-shadow: 0 20px 60px rgba(0,0,0,.2) }
.gf-modal h2 { margin: 0 0 18px; font-size: 20px; color: #1A3461; font-weight: 900 }
.gf-modal-close { position: absolute; top: 12px; right: 12px; background: none; border: 0; font-size: 22px; cursor: pointer; color: #64748b; padding: 6px 10px }
.gf-modal-close:hover { color: #1A3461 }

.gf-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px }
@media (max-width: 640px) { .gf-form-row { grid-template-columns: 1fr } }
.gf-field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px }
.gf-field > span { font-size: 12px; font-weight: 700; color: #475569 }
.gf-field input, .gf-field select, .gf-field textarea { padding: 9px 11px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 14px; font-family: inherit; color: #1A3461 }
.gf-field input:focus, .gf-field select:focus, .gf-field textarea:focus { outline: 0; border-color: #00A8B5; box-shadow: 0 0 0 3px rgba(0,168,181,.12) }
.gf-hint { font-size: 11px; color: #64748b; margin-top: 2px; line-height: 1.4 }
.gf-check { display: flex; flex-direction: column; gap: 4px; padding: 8px 0; cursor: pointer; font-size: 13px; color: #1A3461; font-weight: 600 }
.gf-check input { width: 18px; height: 18px; accent-color: #00A8B5 }
.gf-check > span:first-of-type { display: flex; align-items: center; gap: 8px; font-weight: 700 }

.gf-calc { display: flex; gap: 14px; flex-wrap: wrap; padding: 10px 14px; background: #f0fdfa; border: 1px solid #99f6e4; border-radius: 8px; font-size: 13px; color: #115e59; margin-bottom: 14px }

.gf-form-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 14px; padding-top: 14px; border-top: 1px solid #e2e8f0 }
`
